// Central API client. Speaks the standard `{ data, meta }` response envelope
// and the `{ error: { code, message } }` error shape from apps/api/src/lib/respond.js.
//
// Contract is documented in /CLAUDE.md ("API Response Contract"). Both `/health`
// and the public report viewer at `/r/:token` are excluded from the envelope —
// callers that hit those endpoints should use raw fetch, not this wrapper.

import { ERROR_CODES } from './errorCodes.js';

const BASE = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  constructor({ code, message, status }) {
    super(message || `API error ${status}`);
    this.name = 'ApiError';
    this.code = code || ERROR_CODES.SERVER_ERROR;
    this.status = status;
  }
}

export { ERROR_CODES };

// Low-level wrapper. Returns `{ data, meta }` on success; throws ApiError on
// non-2xx. Per-domain helpers in apps/web/src/api/*.js call this and re-export
// the same envelope so call sites can destructure `data` (and `meta` when
// they need cache freshness or pagination info).
export async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });

  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = body?.error;
    throw new ApiError({
      code: err?.code,
      message: err?.message,
      status: res.status,
    });
  }

  // Backstop for endpoints that haven't migrated yet (or external/raw fetches
  // routed through here by mistake): treat `body` as `data` and synthesise an
  // empty meta. `/health` is the deliberate non-envelope JSON endpoint and
  // shouldn't go through apiFetch.
  if (!body || typeof body !== 'object' || !('data' in body) || !('meta' in body)) {
    return { data: body, meta: {} };
  }
  return { data: body.data, meta: body.meta };
}

export function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
